'use client';

/**
 * SEO Assistant — an agentic chat for one domain's search performance.
 *
 * Same machinery as the Ads Assistant: a dedicated gateway session
 * (`seo-asst-<domain>`) opened with cwd `~/beast`. The on-screen Search
 * Console summary is handed to the agent as model-only grounding on the
 * first turn; for depth it curls the local SEO API (`/api/seo/<domain>` →
 * queries, pages, movers, devices, countries, history) and can crawl the
 * live site.
 *
 * The chat frame itself (session open, transcript, streaming, input row,
 * suggestion chips) lives in the shared AssistantShell — this file only
 * supplies the SEO-specific identity and grounding.
 */
import { AssistantShell } from '@/components/chat/AssistantShell';

export interface SeoOverviewLite {
  clicks?: number;
  clicksChange?: number;
  impressions?: number;
  impressionsChange?: number;
  avgPosition?: number;
  positionChange?: number;
  avgCtr?: number;
  topQuery?: string;
  moversUp?: number;
  moversDown?: number;
  topCountry?: string | null;
  date?: string;
  noData?: boolean;
}

const SUGGESTIONS = [
  'What are the top 3 SEO wins I should chase this week?',
  'Which keywords are on page 2 that I could push to page 1?',
  'Which pages are underperforming and why?',
  'What content should I add or improve next?',
];

function buildGrounding(domain: string, o: SeoOverviewLite | null): string {
  const dashed = domain.replace(/\./g, '-');
  const L: string[] = [];
  L.push(`You are a senior SEO strategist embedded in the Beast dashboard, advising on ONE website: ${domain}. Data source is Google Search Console. Be concise, specific and actionable — talk in concrete numbers, exact keywords/pages, and next steps. Use short markdown (bullets, bold key numbers).`);
  L.push('');
  if (o && !o.noData) {
    L.push(`Search Console summary (latest, ${o.date || 'recent'}):`);
    L.push(`- Clicks ${o.clicks ?? '—'}${o.clicksChange != null ? ` (${o.clicksChange > 0 ? '+' : ''}${o.clicksChange}%)` : ''}, Impressions ${o.impressions ?? '—'}${o.impressionsChange != null ? ` (${o.impressionsChange > 0 ? '+' : ''}${o.impressionsChange}%)` : ''}`);
    L.push(`- Avg position ${o.avgPosition ?? '—'}${o.positionChange ? ` (${o.positionChange > 0 ? 'improved' : 'dropped'} ${Math.abs(o.positionChange)})` : ''}, CTR ${o.avgCtr ?? '—'}%`);
    if (o.topQuery) L.push(`- Top query: "${o.topQuery}"`);
    if (o.moversUp != null || o.moversDown != null) L.push(`- Movers: ${o.moversUp ?? 0} up, ${o.moversDown ?? 0} down`);
    if (o.topCountry) L.push(`- Top country: ${o.topCountry}`);
  } else {
    L.push(`No Search Console data has landed for ${domain} yet (recently verified). Pull it yourself and/or crawl the live site before advising.`);
  }
  L.push('');
  L.push(`For the FULL detail (top 50 keywords with position changes, top pages, movers up/down, device & country breakdowns, and daily history) curl the local Beast API:`);
  L.push(`  curl -s "http://localhost:3000/api/seo/${domain}"`);
  L.push(`You can also fetch the live site (e.g. \`curl -s https://${domain}/\` or a specific page) to inspect titles, headings, content and internal links. The site's project files may live under ~/beast/projects/business/websites/${dashed}/ — read them if useful. Do NOT edit any files or the site unless the user explicitly asks.`);
  return L.join('\n');
}

export function SeoAssistant({ domain, overview }: { domain: string; overview?: SeoOverviewLite | null }) {
  return (
    <AssistantShell
      slug={`seo-asst-${domain.replace(/\./g, '-')}`}
      sessionName={`SEO · ${domain}`}
      sessionIcon="🔎"
      title="SEO Assistant"
      subject={domain}
      emptyGradient="from-purple-500 to-blue-600"
      emptyBlurb="I can read this site's Search Console data, inspect its pages, and recommend SEO improvements."
      suggestions={SUGGESTIONS}
      buildGrounding={() => buildGrounding(domain, overview ?? null)}
      confirmClearText={`Clear this SEO assistant chat for ${domain} and start fresh?`}
    />
  );
}
